<template>
  <div class="v2-downloads-page">
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading downloads..."
      />
    </div>

    <template v-else>
      <div
        v-if="!downloads.length"
        class="v2-flex v2-justify-center v2-p-12"
      >
        <v2-card class="v2-text-center v2-p-8">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="v2-downloads-page__empty-icon"
          >
            <line
              x1="22"
              y1="2"
              x2="11"
              y2="13"
            />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
          <p class="v2-text-muted v2-mt-4">
            There are no downloads yet.
          </p>
        </v2-card>
      </div>

      <div v-else>
        <h1 class="v2-heading-sm v2-mb-4">
          Downloads
        </h1>

        <v2-card :no-padding="true">
          <v2-table
            :columns="tableColumns"
            :items="downloads"
          >
            <template #cell(createdAt)="{ item }">
              <span class="v2-text-sm">
                {{ getDateFromTimestamp(item.createdAt) }}
                {{ getFromattedTimeFromDate(item.createdAt) }}
              </span>
            </template>

            <template #cell(downloadType)="{ item }">
              <span class="v2-text-sm">
                {{ getDownloadTypeName(item.downloadType) }}
              </span>
            </template>

            <template #cell(status)="{ item }">
              <v2-badge :variant="getStatusVariant(item.status)">
                {{ getDownloadStatus(item.status) }}
              </v2-badge>
              <p
                v-if="getErrorReason(item)"
                class="v2-downloads-page__error-reason v2-text-xs v2-text-muted v2-mt-1 v2-mb-0"
              >
                {{ getErrorReason(item) }}
              </p>
            </template>

            <template #cell(downloadUrl)="{ item }">
              <div
                v-if="item.downloadUrl"
                class="v2-flex v2-items-center v2-gap-2"
              >
                <a
                  href="#"
                  class="v2-downloads-page__link"
                  @click.prevent="download(item.downloadUrl)"
                >
                  Download
                </a>
                <button
                  class="v2-downloads-page__copy-btn"
                  title="Copy download link"
                  @click="copyDownloadLink(item.downloadUrl)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                      ry="2"
                    />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
            </template>
          </v2-table>
        </v2-card>

        <v2-pagination
          v-if="hasPagination"
          :current-page="currentPage"
          :total-items="totalDownloads"
          :per-page="perPage"
          @page-change="onPageChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Pagination from '@/components/v2/V2Pagination.vue'
import { useDownloads } from '@/composables/useDownloads'

export default {
  name: 'V2Downloads',
  components: {
    V2Spinner,
    V2Card,
    V2Table,
    V2Badge,
    V2Pagination,
  },
  setup() {
    const dl = useDownloads()
    dl.initDownloads()
    return { ...dl }
  },
  methods: {
    getStatusVariant(status) {
      if (status === this.DOWNLOAD_FILE_STATUS.ERROR.VALUE) return 'destructive'
      if (status === this.DOWNLOAD_FILE_STATUS.READY_TO_DOWNLOAD.VALUE) return 'success'
      return 'default'
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-downloads-page {
  &__empty-icon {
    color: var(--v2-muted-foreground);
    margin: 0 auto;
    display: block;
  }

  &__error-reason {
    max-width: 15rem;
    overflow-wrap: anywhere;
  }

  &__link {
    color: var(--v2-primary);
    font-size: 0.875rem;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__copy-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-muted);
      color: var(--v2-primary);
    }
  }
}
</style>
